<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useEditReturnAdRequest, useGetReturnAdRequest, useSetReturnAdActivityRequest } from '@/api'
import PauseIcon from '@/assets/icons/pause.svg?component'
import PlayIcon from '@/assets/icons/play.svg?component'
import HeaderComponent from '@/components/Navigation/HeaderComponent.vue'
import ReturnAdForm from '@/components/ReturnAdForm/ReturnAdForm.vue'
import ReturnAdPreview from '@/components/ReturnAdPreview/ReturnAdPreview.vue'
import { notification } from '@/lib/helpers'
import { useBackToPreviousPage } from '@/lib/hooks/back-to-previous-page.hooks'
import { usePreviewData } from '@/lib/hooks/provider.hooks'
import { type ReturnAdFormType, ReturnAdStatusEnum } from '@/lib/types/return-ad.types'
import { useUserStore } from '@/stores/user'
import { BoxComponent, ButtonComponent, FlexComponent, LoadingComponent } from '@/ui-library'

usePreviewData()
const route = useRoute()

const handleBack = useBackToPreviousPage({ name: 'returnAds' })
const userStore = useUserStore()
const returnAdId = Number(route.params.id as unknown as string)

const { data, isLoading } = useGetReturnAdRequest(returnAdId)
const { editReturnAd, isLoading: isEditingLoading, error: editError } = useEditReturnAdRequest(returnAdId)
const { setStatus, isLoading: isStatusLoading } = useSetReturnAdActivityRequest(returnAdId)

const isReturnAdActive = ref(false)
const isPauseBtnShown = computed(() => {
  return (
    data?.value?.status && [ReturnAdStatusEnum.Active, ReturnAdStatusEnum.Paused, ReturnAdStatusEnum.Scheduled].includes(data.value?.status)
  )
})

watch(
  () => data.value?.status,
  () => {
    isReturnAdActive.value = data.value?.status === ReturnAdStatusEnum.Active || data.value?.status === ReturnAdStatusEnum.Scheduled
  },
)

const onReturnAdEdit = async (formData: ReturnAdFormType) => {
  await editReturnAd(formData)
  if (!editError.value) {
    notification.success('The return ad was saved successfully')
    await handleBack()
  }
}

const setReturnAdActiveStatus = async () => {
  const shouldPause = isReturnAdActive.value
  await setStatus(shouldPause)
  isReturnAdActive.value = !isReturnAdActive.value
}
</script>

<template>
  <HeaderComponent>
    <ButtonComponent
      v-if="isPauseBtnShown"
      variant="transparent"
      :is-loading="isStatusLoading"
      :is-disabled="isStatusLoading"
      @click="setReturnAdActiveStatus"
    >
      <template #iconLeft>
        <el-icon :size="24">
          <PauseIcon v-if="isReturnAdActive" />
          <PlayIcon v-else />
        </el-icon>
      </template>
      {{ isReturnAdActive ? 'Pause' : 'Un-pause' }} return ad
    </ButtonComponent>
  </HeaderComponent>
  <LoadingComponent v-if="userStore.isLoading" :size="64" :is-full-size="true" />
  <template v-if="userStore.user">
    <FlexComponent justify="space-between" class="wrapper" :gap="40">
      <BoxComponent w="50%">
        <ReturnAdForm
          v-if="data"
          :return-ad="data"
          :is-loading="isLoading || isEditingLoading"
          :default-advertiser-group-id="userStore.user.storeGroupId"
          :is-edit-mode="true"
          @handle-submit="onReturnAdEdit"
          @handle-back="handleBack"
        />
      </BoxComponent>
      <BoxComponent w="50%">
        <ReturnAdPreview />
      </BoxComponent>
    </FlexComponent>
  </template>
</template>

<style lang="scss" scoped>
.wrapper {
  max-width: 1320px;
  margin: 0 auto;
  padding: 40px 20px;
}
.preview {
  position: sticky;
  top: 114px;
  left: 0;
}
</style>
